import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Minus, Plus } from "lucide-react"

/** Zoom steps for the unified `DeclarationDocumentViewer` panes in the
 *  conformity editor (the frozen-PDF pane header + the PDF overlay). Not
 *  reused from Studio V2's own `ZoomControl`/`Zoom` (studio-v2/document/):
 *  that type is imported from Studio's own URL-state module
 *  (`studio-v2/studioV2Url`), and its `.sv2-zoom`/`.sv2-zb` styling lives in
 *  `studioV2.css`, which this feature does not load. */
export const PDF_PANE_ZOOM_LEVELS = [0.6, 0.8, 1, 1.25, 1.5, 2] as const
export type PdfPaneZoom = (typeof PDF_PANE_ZOOM_LEVELS)[number]
export const PDF_PANE_ZOOM_DEFAULT: PdfPaneZoom = 1

export interface PdfPaneZoomControlProps {
  zoom: PdfPaneZoom
  onChange: (zoom: PdfPaneZoom) => void
}

/** `− 100% +` zoom stepper, styled like the existing `ConformityPreviewPanel`
 *  toolbar (grey5/white icon buttons, mono 11px percentage) rather than
 *  Studio V2's own control — see the module doc comment for why. */
export function PdfPaneZoomControl({
  zoom,
  onChange,
}: PdfPaneZoomControlProps) {
  const { translate } = useTranslation()
  const index = PDF_PANE_ZOOM_LEVELS.indexOf(zoom)
  const canZoomOut = index > 0
  const canZoomIn = index < PDF_PANE_ZOOM_LEVELS.length - 1
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        aria-label={translate("MC_CS_CONF_EDIT_ARIA_ZOOM_OUT")}
        disabled={!canZoomOut}
        onClick={() => canZoomOut && onChange(PDF_PANE_ZOOM_LEVELS[index - 1])}
        className="rounded p-1 text-grey5 hover:bg-white/5 hover:text-white disabled:opacity-40"
      >
        <Minus size={14} />
      </button>
      <span className="font-mono text-[11px] text-grey5">
        {Math.round(zoom * 100)}%
      </span>
      <button
        type="button"
        aria-label={translate("MC_CS_CONF_EDIT_ARIA_ZOOM_IN")}
        disabled={!canZoomIn}
        onClick={() => canZoomIn && onChange(PDF_PANE_ZOOM_LEVELS[index + 1])}
        className="rounded p-1 text-grey5 hover:bg-white/5 hover:text-white disabled:opacity-40"
      >
        <Plus size={14} />
      </button>
    </div>
  )
}

export interface PdfPanePageIndicatorProps {
  current: number | null
  total: number | null
}

/** Scroll-derived "p. cur/total" readout, fed by
 *  `DeclarationDocumentViewer`'s `onCurrentPage`. Reuses the SAME
 *  translation key Studio V2's own `PageIndicator` uses
 *  (`MC_DECL_V2_PAGE_INDICATOR` is plain declaration-review copy, not
 *  Studio-scoped). Renders nothing before the viewer's first report. */
export function PdfPanePageIndicator({
  current,
  total,
}: PdfPanePageIndicatorProps) {
  const { translate } = useTranslation()
  if (!current || !total) return null
  return (
    <span className="font-mono text-[11px] text-grey5">
      {translate("MC_DECL_V2_PAGE_INDICATOR", { cur: current, total })}
    </span>
  )
}
